import React, { useRef, useState, useEffect } from 'react';
import { X, CheckCircle, Eraser } from '@phosphor-icons/react';

/**
 * Touch-friendly signature capture modal.
 * Returns { signature_data_url, client_name } via onSave.
 */
export const SignatureModal = ({ title = 'Signature client', reservationTitle = '', onClose, onSave, saving = false }) => {
  const canvasRef = useRef(null);
  const [clientName, setClientName] = useState('');
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Fit canvas to displayed size (HiDPI aware)
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1E2030';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;
    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      if (e.touches) return { x: e.touches[0].clientX - r.left, y: e.touches[0].clientY - r.top };
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasStroke(true); };
    const end = () => { drawing = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', end);
      canvas.removeEventListener('mouseleave', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
  };

  const save = () => {
    if (!hasStroke) return;
    if (!clientName.trim()) return;
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave({ signature_data_url: dataUrl, client_name: clientName.trim() });
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10002, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}
      data-testid="signature-modal"
    >
      <div style={{ background: 'white', borderRadius: '10px', maxWidth: '640px', width: '100%', maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
            {reservationTitle && <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: '2px' }}>{reservationTitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66' }} aria-label="Fermer"><X size={20} /></button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <label className="form-label" style={{ fontSize: '0.82rem' }}>Nom du client *</label>
          <input
            type="text"
            className="form-input"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Ex : M. Dupont"
            data-testid="signature-client-name"
          />
          <div style={{ marginTop: '14px', fontSize: '0.82rem', color: '#5E5F66' }}>Signature :</div>
          <div style={{ border: '2px dashed #C2C3C9', borderRadius: '8px', marginTop: '6px', background: '#FAFAFA', touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              style={{ width: '100%', height: '220px', display: 'block', cursor: 'crosshair', borderRadius: '6px' }}
            />
          </div>
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <button type="button" onClick={clear} data-testid="signature-clear" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: '1px solid #E2E2E6', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', color: '#5E5F66', fontSize: '0.82rem' }}>
              <Eraser size={13} /> Effacer
            </button>
          </div>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving} data-testid="signature-cancel">Annuler</button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={saving || !hasStroke || !clientName.trim()}
            data-testid="signature-save"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <CheckCircle size={15} weight="bold" /> {saving ? 'Enregistrement...' : 'Valider et signer'}
          </button>
        </div>
      </div>
    </div>
  );
};
